import { Typography, Card, Tag, Button, Space, Skeleton, Spin, message, Popconfirm, Collapse, Row, Col, Divider } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  AuditOutlined,
  SettingOutlined,
  ExperimentOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { opportunityApi, type AssignedCandidate } from '@/api/opportunityApi';
import { documentApi, type DocumentDto } from '@/api/documentApi';
import { useWorkflow } from '@/hooks/useWorkflow';
import { useActionItemStore } from '@/store/actionItemStore';
import type { Opportunity, OpportunityStatus } from '@/types/opportunity';
import WorkflowTracker from '@/components/workflow/WorkflowTracker';
import TransitionHistory from '@/components/workflow/TransitionHistory';
import PipelineSummary from '@/components/workflow/PipelineSummary';
import BidAnalysisAI from '@/components/workflow/BidAnalysisAI';
import KickoffAIPlanner from '@/components/workflow/KickoffAIPlanner';
import ProposalAIReview from '@/components/workflow/ProposalAIReview';
import DeadlineCountdown from '@/components/common/DeadlineCountdown';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import { parseDateLocal } from '@/utils/dateFormatters';

const { Text, Link } = Typography;

const statusColors: Record<OpportunityStatus, string> = {
  NEW: 'blue',
  INTAKE_COMPLETE: 'cyan',
  QUALIFIED: 'geekblue',
  BID_GO: 'green',
  BID_NO_GO: 'red',
  PLANNING: 'purple',
  CONTENT_DEV: 'orange',
  IN_REVIEW: 'gold',
  CEO_REVIEW: 'volcano',
  SUBMITTED: 'lime',
  POST_SUBMISSION: 'magenta',
  WON: 'success',
  LOST: 'error',
  CANCELLED: 'default',
};

const statusLabels: Record<OpportunityStatus, string> = {
  NEW: 'New',
  INTAKE_COMPLETE: 'Intake Complete',
  QUALIFIED: 'Qualified',
  BID_GO: 'Bid - Go',
  BID_NO_GO: 'Bid - No Go',
  PLANNING: 'Planning',
  CONTENT_DEV: 'Content Dev',
  IN_REVIEW: 'In Review',
  CEO_REVIEW: 'CEO Review',
  SUBMITTED: 'Submitted',
  POST_SUBMISSION: 'Post Submission',
  WON: 'Won',
  LOST: 'Lost',
  CANCELLED: 'Cancelled',
};

const OpportunityDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [rfoDocument, setRfoDocument] = useState<DocumentDto | null>(null);
  const [assignedCandidates, setAssignedCandidates] = useState<AssignedCandidate[]>([]);

  const { workflowStatus, reviewGates, loading: wfLoading, advancePhase, refresh: refreshWorkflow } = useWorkflow(id);
  const refreshActionItemCount = useActionItemStore((s) => s.fetchCount);

  const fetchAssignedCandidates = useCallback(async () => {
    if (!id) return;
    try {
      const data = await opportunityApi.getAssignedCandidates(id);
      setAssignedCandidates(data);
    } catch {
      // no assigned candidates
    }
  }, [id]);

  const fetchOpportunity = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await opportunityApi.getById(id);
      setOpportunity(data);
    } catch (error) {
      console.error('Failed to fetch opportunity:', error);
      message.error('Failed to load opportunity details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchRfoDocument = useCallback(async () => {
    if (!id) return;
    try {
      const docs = await documentApi.getByOpportunityId(id);
      const rfoSource = docs.find((d) => d.category === 'RFO_SOURCE');
      setRfoDocument(rfoSource || null);
    } catch {
      // Documents may not exist yet
    }
  }, [id]);

  useEffect(() => {
    fetchOpportunity();
    fetchRfoDocument();
    fetchAssignedCandidates();
  }, [fetchOpportunity, fetchRfoDocument, fetchAssignedCandidates]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await opportunityApi.delete(id);
      message.success('Opportunity deleted');
      navigate('/opportunities');
    } catch (error) {
      console.error('Failed to delete opportunity:', error);
      message.error('Failed to delete opportunity');
    }
  };

  const handleAdvance = async (reason: string) => {
    await advancePhase(reason);
    fetchOpportunity();
  };

  const onRefresh = useCallback(() => {
    refreshWorkflow();
    fetchRfoDocument();
    refreshActionItemCount();
  }, [refreshWorkflow, fetchRfoDocument, refreshActionItemCount]);

  const formatDate = (value?: string) => {
    if (!value) return '-';
    // Date-only strings ("YYYY-MM-DD") must be parsed in local time to avoid
    // a UTC-to-local off-by-one display in west-of-UTC timezones.
    return parseDateLocal(value).format('MMM D, YYYY');
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '12px 20px' }}>
        <div className="ui-surface" style={{ padding: 20, marginBottom: 16 }}>
          <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 1, width: ['60%'] }} />
        </div>
        <Row gutter={16}>
          <Col xs={24} lg={16}>
            <div className="ui-surface" style={{ padding: 20 }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="ui-surface" style={{ padding: 20 }}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div style={{ padding: '12px 20px' }}>
        <div className="ui-surface">
          <EmptyState
            icon={<InfoCircleOutlined />}
            title="Opportunity not found"
            description="This opportunity may have been deleted or you may not have access to it."
            action={
              <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate('/opportunities')}>
                Back to Opportunities
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 20px' }}>
      {/* Header */}
      <PageHeader
        title={
          <Space align="center" size={8}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/opportunities')}
              style={{ padding: '4px 8px', marginLeft: -8 }}
            />
            <span>{opportunity.title}</span>
          </Space>
        }
        subtitle={
          <Space size={8} wrap>
            <Tag color={statusColors[opportunity.status] || 'default'} style={{ margin: 0 }}>
              {statusLabels[opportunity.status] || opportunity.status}
            </Tag>
            {opportunity.agencyName && (
              <Text type="secondary" style={{ fontSize: 12 }}>{opportunity.agencyName}</Text>
            )}
            {opportunity.rfoNumber && (
              <Text type="secondary" style={{ fontSize: 12 }}>#{opportunity.rfoNumber}</Text>
            )}
            {(() => {
              const currentPhaseInfo = workflowStatus?.phases.find(p => p.status === 'current');
              if (!currentPhaseInfo?.phaseDeadline) return null;
              return (
                <DeadlineCountdown
                  deadline={currentPhaseInfo.phaseDeadline}
                  label={`${currentPhaseInfo.gateLabel || currentPhaseInfo.displayName} due`}
                  showTooltip={false}
                />
              );
            })()}
          </Space>
        }
        actions={
          <Popconfirm
            title="Delete this opportunity?"
            description="This action cannot be undone."
            onConfirm={handleDelete}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Button danger size="small" icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        }
      />

      {/* Two-column layout */}
      <Row gutter={16}>
        {/* ═══ LEFT COLUMN: Workflow & Phase Content + Transition History ═══ */}
        <Col xs={24} lg={16} xl={16}>
          {workflowStatus && id && (
            <WorkflowTracker
              workflowStatus={workflowStatus}
              reviewGates={reviewGates}
              opportunityId={id}
              onAdvance={handleAdvance}
              onRefresh={onRefresh}
            />
          )}

          {wfLoading && !workflowStatus && (
            <Card style={{ marginBottom: 12, textAlign: 'center' }}>
              <Spin size="small" /> <Text type="secondary">Loading workflow...</Text>
            </Card>
          )}

          {/* Transition History — at bottom of left column */}
          {workflowStatus && workflowStatus.transitions && workflowStatus.transitions.length > 0 && (
            <Card
              size="small"
              style={{ marginTop: 12, marginBottom: 12, borderRadius: 8 }}
            >
              <Collapse
                ghost
                size="small"
                items={[{
                  key: 'history',
                  label: (
                    <Space>
                      <HistoryOutlined style={{ color: 'var(--color-warning)' }} />
                      <Text strong style={{ fontSize: 13 }}>
                        Phase Transition History ({workflowStatus.transitions.length})
                      </Text>
                    </Space>
                  ),
                  children: <TransitionHistory transitions={workflowStatus.transitions} />,
                }]}
              />
            </Card>
          )}

        </Col>

        {/* ═══ RIGHT COLUMN: Details, Phase Management, Pipeline Journey ═══ */}
        <Col xs={24} lg={8} xl={8}>
          <div style={{ position: 'sticky', top: 12 }}>
            {/* Opportunity Details */}
            <Card
              size="small"
              style={{ marginBottom: 12, borderRadius: 8 }}
              title={
                <Space>
                  <InfoCircleOutlined style={{ color: 'var(--color-primary)' }} />
                  <Text strong style={{ fontSize: 13 }}>Opportunity Details</Text>
                </Space>
              }
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 12 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Due Date</Text>
                  <Text strong>{formatDate(opportunity.dueDate)}</Text>
                  {opportunity.dueTime && <Text type="secondary"> {opportunity.dueTime}</Text>}
                </div>
                {workflowStatus && (() => {
                  const currentPhaseInfo = workflowStatus.phases.find(p => p.status === 'current');
                  if (!currentPhaseInfo) return null;
                  const phaseTitle = currentPhaseInfo.gateLabel || currentPhaseInfo.displayName;
                  return (
                    <div>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                        Phase Deadline {phaseTitle ? `(${phaseTitle})` : ''}
                      </Text>
                      {currentPhaseInfo.phaseDeadline ? (
                        <Space size={6} wrap>
                          <Text strong>{parseDateLocal(currentPhaseInfo.phaseDeadline).format('MMM D, YYYY')}</Text>
                          <DeadlineCountdown deadline={currentPhaseInfo.phaseDeadline} showTooltip={false} />
                        </Space>
                      ) : (
                        <Text type="secondary" style={{ fontStyle: 'italic' }}>Not set</Text>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Agency</Text>
                  <Text>{opportunity.agencyName || '-'}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>RFO Number</Text>
                  <Text>{opportunity.rfoNumber || '-'}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Source Portal</Text>
                  <Text>{opportunity.sourcePortal || '-'}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Submission Method</Text>
                  <Text>{opportunity.submissionMethod || '-'}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Discovery Date</Text>
                  <Text>{formatDate(opportunity.discoveryDate)}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Discovered By</Text>
                  <Text>{opportunity.discoveredBy || '-'}</Text>
                </div>
                {opportunity.awardDate && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Award Date</Text>
                    <Text strong>{formatDate(opportunity.awardDate)}</Text>
                    {opportunity.awardTime && <Text type="secondary"> {opportunity.awardTime}</Text>}
                  </div>
                )}
                {opportunity.extendedSubmissionDate && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Extended Submission Date</Text>
                    <Text strong>{formatDate(opportunity.extendedSubmissionDate)}</Text>
                  </div>
                )}
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Content Library</Text>
                  <Link style={{ fontSize: 12 }} onClick={() => navigate('/content-library')}>
                    <FileTextOutlined /> View Documents
                  </Link>
                </div>
              </div>
              {opportunity.description && (
                <>
                  <Divider style={{ margin: '10px 0' }} />
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>Description</Text>
                  <Text style={{ fontSize: 12 }}>{opportunity.description}</Text>
                </>
              )}
            </Card>

            {/* AI Analysis — phase-specific, always visible */}
            {workflowStatus && id && (
              <Card
                size="small"
                style={{ marginBottom: 12, borderRadius: 8, borderLeft: '3px solid var(--color-purple)' }}
                title={
                  <Space>
                    <ExperimentOutlined style={{ color: 'var(--color-purple)' }} />
                    <Text strong style={{ fontSize: 13 }}>AI Analysis</Text>
                    <Tag color="purple" style={{ fontSize: 10 }}>
                      {workflowStatus.currentPhase.replace('PHASE_', 'Gate ')}
                    </Tag>
                  </Space>
                }
                styles={{ body: { padding: '8px 12px' } }}
              >
                {workflowStatus.currentPhase === 'PHASE_A' && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Upload RFO documents to auto-extract requirements, resource roles, and compliance criteria using AI.
                  </Text>
                )}
                {workflowStatus.currentPhase === 'PHASE_B' && (
                  <BidAnalysisAI opportunityId={id} />
                )}
                {workflowStatus.currentPhase === 'PHASE_C' && (
                  <KickoffAIPlanner opportunityId={id} />
                )}
                {workflowStatus.currentPhase === 'PHASE_D' && (
                  <>
                    <ProposalAIReview opportunityId={id} />
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Upload proposal drafts for AI scoring. Use resume analysis to evaluate assigned candidates against extracted role requirements.
                      </Text>
                    </div>
                  </>
                )}
                {workflowStatus.currentPhase === 'PHASE_E' && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Review stage — all sub-gate reviews (Technical, Strategy, Compliance, Executive) must be completed.
                  </Text>
                )}
                {workflowStatus.currentPhase === 'PHASE_F' && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    CEO review and sign-off required before proceeding to submission.
                  </Text>
                )}
              </Card>
            )}

            {/* Assigned Candidates */}
            {assignedCandidates.length > 0 && (
              <Card
                size="small"
                style={{ marginBottom: 12, borderRadius: 8, borderLeft: '3px solid #2563EB' }}
                title={
                  <Space>
                    <TeamOutlined style={{ color: '#2563EB' }} />
                    <Text strong style={{ fontSize: 13 }}>Assigned Resources</Text>
                    <Tag color="blue" style={{ fontSize: 10 }}>{assignedCandidates.length}</Tag>
                  </Space>
                }
                styles={{ body: { padding: '8px 12px' } }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {assignedCandidates.map((c) => (
                    <div key={`${c.candidateId}-${c.assignmentRole}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: '#2563EB', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 600, flexShrink: 0,
                      }}>
                        <UserOutlined />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 12, display: 'block' }}>{c.candidateName}</Text>
                        {c.assignmentRole && (
                          <Text type="secondary" style={{ fontSize: 11 }}>{c.assignmentRole}</Text>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Phase Management & Pipeline Journey */}
            {workflowStatus && id && (
              <Card
                size="small"
                style={{ marginBottom: 12, borderRadius: 8 }}
                title={
                  <Space>
                    <SettingOutlined style={{ color: 'var(--color-purple)' }} />
                    <Text strong style={{ fontSize: 13 }}>Phase Management & Pipeline</Text>
                  </Space>
                }
                styles={{ body: { padding: '8px 12px' } }}
              >
                <PipelineSummary
                  workflowStatus={workflowStatus}
                  reviewGates={reviewGates}
                  opportunityId={id}
                  onRefresh={onRefresh}
                />
              </Card>
            )}

            {/* RFO Source Document */}
            {rfoDocument && (
              <Card
                size="small"
                style={{ marginBottom: 12, borderRadius: 8, borderLeft: '3px solid var(--color-primary)' }}
              >
                <Space>
                  <FileTextOutlined style={{ color: 'var(--color-primary)' }} />
                  <div>
                    <Text strong style={{ fontSize: 12 }}>RFO Document</Text>
                    <br />
                    <Link
                      style={{ fontSize: 12 }}
                      onClick={async () => {
                        try {
                          await documentApi.downloadFile(rfoDocument.id, rfoDocument.fileName);
                        } catch {
                          message.error('Failed to download file');
                        }
                      }}
                    >
                      {rfoDocument.fileName}
                    </Link>
                    <Text type="secondary" style={{ fontSize: 11 }}> ({(rfoDocument.fileSizeBytes / 1024).toFixed(0)} KB)</Text>
                  </div>
                </Space>
              </Card>
            )}

            {/* Audit Information */}
            <Card
              size="small"
              style={{ marginBottom: 12, borderRadius: 8 }}
            >
              <Collapse
                ghost
                size="small"
                items={[{
                  key: 'audit',
                  label: (
                    <Space>
                      <AuditOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                      <Text strong style={{ fontSize: 13 }}>Audit Information</Text>
                    </Space>
                  ),
                  children: (
                    <div style={{ fontSize: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Created</Text>
                          <Text>{formatDateTime(opportunity.createdAt)}</Text>
                        </div>
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Last Updated</Text>
                          <Text>{formatDateTime(opportunity.updatedAt)}</Text>
                        </div>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Opportunity ID</Text>
                        <code style={{ fontSize: 11 }}>{opportunity.id}</code>
                      </div>
                    </div>
                  ),
                }]}
              />
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default OpportunityDetailPage;
